import { parseDateTimeFromSAPI } from './sapi';
import { type SAPIDate, type SAPIDateTime } from '@/types/sapi/scalars';
import { useMainStore } from '@/stores/main';
import { splitDuration } from './time';

/**
 * Format a tracker duration into a primary and optional secondary display string.
 *
 * Examples (with english abbreviations):
 *   - 45s  → primary "45s",  secondary null
 *   - 5m 12s → primary "5m", secondary "12s"
 *   - 2h 14m → primary "2h", secondary "14m"
 */
export const formatTrackerDuration = (
	totalSeconds: number
): { primary: string; secondary: string | null } => {
	const { t } = useMainStore().translation;
	const { hours, minutes, seconds } = splitDuration(totalSeconds);

	if (hours > 0) {
		return {
			primary: `${hours}${t('general.hourShort')}`,
			secondary: `${minutes}${t('general.minutesExtraShort')}`,
		};
	}

	if (minutes > 0) {
		return {
			primary: `${minutes}${t('general.minutesExtraShort')}`,
			secondary: `${seconds}${t('general.secondsShort')}`,
		};
	}

	return {
		primary: `${seconds}${t('general.secondsShort')}`,
		secondary: null,
	};
};

export const shortDurationValue = (value: number) => {
	const totalMinutes = Math.floor(value / 60);
	const prefix = totalMinutes < 0 ? '-' : '';
	const absoluteMinutes = Math.abs(totalMinutes);

	return `${prefix}${Math.floor(absoluteMinutes / 60)}:${`${
		absoluteMinutes % 60
	}`.padStart(2, '0')}`;
};

/**
 * The short duration shows the hours and minutes delimited by a colon.
 *
 * For example: 13:13h
 *
 * @param value The number of seconds to format
 *
 * @returns DataPair format required by `SuiSummaryTopic`
 */
export const shortDuration = (value: number) => {
	const { t } = useMainStore().translation;
	return [
		{
			value: shortDurationValue(value),
			unit: t('hourShort'),
		},
	];
};

/**
 * Shows duration in a decimal format
 * @param value The number of seconds
 *
 * @returns Object consisting of duration value in decimal and unit
 */
export const decimalDuration = (value: number) => {
	const { t } = useMainStore().translation;
	return {
		value: Math.round((value / 3600) * 100) / 100,
		unit: t('hourShort'),
	};
};

/**
 * Returns text with currency added
 *
 * For example: 1234 €
 *
 * @param text The text of numbers, for example, '1,499'
 * @param currency Currency from API, for example, 'USD' or 'EUR'
 * @param withSpace Adds space between text and currency
 *
 * @returns string with currency added
 */
export const addCurrency = (
	text: string,
	currency: string | undefined,
	withSpace = true
) => {
	switch (currency) {
		case 'GBP':
			return withSpace ? `${text} £` : `${text}£`;
		case 'EUR':
			return withSpace ? `${text} €` : `${text}€`;
		case 'AUD':
			return withSpace ? `AU$ ${text}` : `AU$${text}`;
		case 'USD':
		case 'CAD':
			return withSpace ? `$ ${text}` : `$${text}`;
		case undefined:
			return text;
		default:
			return withSpace ? `${text} ${currency}` : `${text}${currency}`;
	}
};

/**
 * Shows date and time difference depending on the length.
 *
 * @param date The date ar string from Sapi to check for time difference
 * @param roundUp Uses floor by default, ceil if roundUp is true
 *
 * @returns datapair {value, unit}
 */
export const dateTimeDiffDataPair = (
	date: Date | SAPIDate | SAPIDateTime,
	{
		roundUp = false,
	}: {
		roundUp?: boolean;
	} = {}
) => {
	const { t } = useMainStore().translation;
	const dateObject =
		typeof date === 'string' ? parseDateTimeFromSAPI(date) : date;

	let diffInSeconds = (Date.now() - dateObject.getTime()) / 1000;
	let unit = '';
	let diff = 0;

	if (diffInSeconds < 0) {
		diffInSeconds = Math.abs(diffInSeconds);
	}

	if (diffInSeconds < 60) {
		diff = roundUp ? Math.ceil(diffInSeconds) : Math.floor(diffInSeconds);
		unit = diff === 1 ? t('second') : t('seconds');
	} else if (diffInSeconds < 3600) {
		diff = roundUp
			? Math.ceil(diffInSeconds / 60)
			: Math.floor(diffInSeconds / 60);
		unit = diff === 1 ? t('minute') : t('minutes');
	} else if (diffInSeconds < 86400) {
		diff = roundUp
			? Math.ceil(diffInSeconds / 3600)
			: Math.floor(diffInSeconds / 3600);
		unit = diff === 1 ? t('hour') : t('hours');
	} else {
		diff = roundUp
			? Math.ceil(diffInSeconds / 86400)
			: Math.floor(diffInSeconds / 86400);
		unit = diff === 1 ? t('day') : t('days_amount');
	}

	return {
		value: diff,
		unit: unit,
	};
};

/**
 * Shows date and time difference depending on the length.
 *
 * @param date The date ar string from Sapi to check for time difference
 * @param translate Returns full translation string, e.g, "in 1 hour" if true, otherwise just units, e.g., "1 hour", default is true
 * @param roundUp Uses floor by default, ceil if roundUp is true
 *
 * @returns translated string, e.g., "in 3 hours"
 */
export const dateTimeDiff = (
	date: Date | SAPIDate | SAPIDateTime,
	{
		translate = true,
		roundUp = false,
	}: {
		translate?: boolean;
		roundUp?: boolean;
	} = {}
) => {
	const { t } = useMainStore().translation;
	const diffDataPair = dateTimeDiffDataPair(date, { roundUp });

	if (translate) {
		const dateObject =
			typeof date === 'string' ? parseDateTimeFromSAPI(date) : date;

		const diffInSeconds = (Date.now() - dateObject.getTime()) / 1000;
		let phrase = 'general.x_timeunit_ago';

		if (diffInSeconds < 0) {
			phrase = 'general.in_x_timeunit';
		}

		return t(phrase, {
			time: diffDataPair.value,
			unit: diffDataPair.unit,
		});
	}

	return `${diffDataPair.value} ${diffDataPair.unit}`;
};

/**
 * Why the timezone adjustment? Here's an example:
 * date = 2021-02-14
 * machines timezone = -4
 * the js date instance = Thu Feb 13 2021 20:00:00 GMT-0400 (Atlantic Standard Time)
 */
const adjustDateForTimezone = (date: Date): Date => {
	date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

	return date;
};

export const resetTime = (date: Date | string | number): Date => {
	let dateToUse = date;
	let resetDate = new Date(dateToUse);

	if (typeof date === 'string') {
		dateToUse = date.split(/[' ',T]/)[0];
		resetDate = adjustDateForTimezone(new Date(dateToUse));
	}

	resetDate.setHours(0, 0, 0, 0);

	return resetDate;
};

export const resetSapiDateString = (date: string): Date => {
	const splitDate = date.split(/T/);
	const dateToUse = splitDate[0];
	const resetDate = adjustDateForTimezone(new Date(dateToUse));

	if (splitDate.length > 1) {
		const timeToUse = splitDate[1].split(/[+-]/)[0]; // Extract time part before any timezone offset
		const [hours, minutes, seconds] = timeToUse.split(':').map(Number);
		resetDate.setHours(hours || 0, minutes || 0, seconds || 0);
	} else {
		resetDate.setHours(0, 0, 0);
	}

	return resetDate;
};
